// HttpExchangeCard
// -----------------
// Renders one observed HTTP request/response exchange from a sub-finding's
// httpExchanges list as a Postman-style card: label pill + method badge +
// URL + status chip + Copy-as-curl on the header strip; split-panel body
// with request headers/body on the left and response headers/body on the
// right.
//
// Design ref: [[evidence-card-design-2026-07-09]] — Baseline/Payload/Control
// three-card layout, Payload gets a red ring so the eye lands on it.
//
// Honesty principle: renders only observed data. Empty body/header lists
// stay empty; never fabricate missing content. Truncation flag surfaces
// as an amber notice pill so the reader knows the excerpt was capped.

import React, { useMemo, useState } from "react";
import { Copy, Check } from "lucide-react";

import type { HttpExchange, HttpHeader } from "@/models/vulnerability";

interface HttpExchangeCardProps {
  exchange: HttpExchange;
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

// Two visual tiers:
//   * FINDING_STYLE — red ring/badge/border. Used for the exchange(s)
//     that represent the vulnerability itself so the eye lands on them.
//   * NEUTRAL_STYLE — grey. Used for baseline, control, and discovery
//     legs (e.g. Union-based ORDER BY probes) that are context, not the
//     finding.
const FINDING_STYLE = {
  pill: "bg-red-100 text-red-800",
  strip: "bg-red-50 border-red-200",
  border: "border-red-200",
  ring: "ring-1 ring-red-100",
};

const NEUTRAL_STYLE = {
  pill: "bg-slate-100 text-slate-700",
  strip: "bg-gray-50 border-gray-200",
  border: "border-gray-200",
  ring: "",
};

// A label represents the finding when it names a payload (error-based
// "Payload", time-based "Payload (sleep)", boolean-based "TRUE payload"
// / "FALSE payload") or the union-select success ("Union SELECT" in the
// union-based flow). Everything else — baseline, control, discovery
// probes like "ORDER BY (low)" — stays neutral. Case-insensitive match
// so future label variants don't silently miss styling.
function isFindingLabel(label?: string): boolean {
  if (!label) return false;
  const lower = label.toLowerCase();
  if (lower.includes("payload")) return true;
  if (lower === "union select") return true;
  return false;
}

function getExchangeLabelStyle(label?: string) {
  return isFindingLabel(label) ? FINDING_STYLE : NEUTRAL_STYLE;
}

// Status chip color — green for 2xx, amber for 3xx/4xx, red for 5xx.
// Zero (no status observed) renders as grey.
function getStatusChipClasses(status: number): string {
  if (!status) return "bg-gray-100 text-gray-500";
  if (status >= 500) return "bg-red-100 text-red-800";
  if (status >= 400) return "bg-amber-100 text-amber-800";
  if (status >= 300) return "bg-amber-50 text-amber-700";
  if (status >= 200) return "bg-emerald-50 text-emerald-700";
  return "bg-gray-100 text-gray-500";
}

// ---------------------------------------------------------------------------
// Copy-as-curl builder
// ---------------------------------------------------------------------------

// Shell-quotes a value using single quotes; single quotes inside the value
// are broken out with the standard bash idiom '\''.
function shellQuote(value: string): string {
  return `'${value.replace(/'/g, "'\\''")}'`;
}

function buildCurlCommand(exchange: HttpExchange): string {
  const parts: string[] = [];
  parts.push(`curl -i -X ${exchange.method || "GET"}`);
  if (exchange.requestHeaders) {
    for (const header of exchange.requestHeaders) {
      parts.push(`  -H ${shellQuote(`${header.name}: ${header.value}`)}`);
    }
  }
  if (exchange.requestBody) {
    parts.push(`  --data-raw ${shellQuote(exchange.requestBody)}`);
  }
  parts.push(`  ${shellQuote(exchange.url)}`);
  return parts.join(" \\\n");
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function HeadersList({ headers }: { headers?: HttpHeader[] }) {
  if (!headers || headers.length === 0) {
    return (
      <div className="font-mono text-xs text-gray-400 italic">
        No headers captured.
      </div>
    );
  }
  return (
    <div className="font-mono text-xs text-gray-800 space-y-0.5 break-all">
      {headers.map((header, index) => (
        <div key={`${header.name}-${index}`}>
          <span className="text-gray-500">{header.name}:</span> {header.value}
        </div>
      ))}
    </div>
  );
}

function BodyPanel({ body, truncated }: { body?: string; truncated?: boolean }) {
  if (!body) {
    return (
      <div className="font-mono text-xs text-gray-400 italic bg-gray-50 border border-gray-200 rounded-md p-2">
        No body captured.
      </div>
    );
  }
  return (
    <>
      <pre className="font-mono text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-md p-2 whitespace-pre-wrap break-all max-h-72 overflow-y-auto">
        {body}
      </pre>
      {truncated && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-2">
          Response body truncated to 2 KB.
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const HttpExchangeCard: React.FC<HttpExchangeCardProps> = ({ exchange }) => {
  const [copied, setCopied] = useState(false);
  const curlCommand = useMemo(() => buildCurlCommand(exchange), [exchange]);

  const labelStyle = getExchangeLabelStyle(exchange.label);
  const statusChipClasses = getStatusChipClasses(exchange.responseStatus);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(curlCommand);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard denied — fall back to a no-op; the user can still
      // read the request/response fields inline.
    }
  };

  const hasRequestBody = !!exchange.requestBody;
  const hasResponseBody = !!exchange.responseBodyExcerpt;

  return (
    <div className={`border rounded-lg overflow-hidden ${labelStyle.border} ${labelStyle.ring}`}>
      <div className={`flex items-center gap-3 px-4 py-2.5 border-b ${labelStyle.strip}`}>
        {exchange.label && (
          <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${labelStyle.pill}`}>
            {exchange.label}
          </span>
        )}
        <span className="inline-flex items-center rounded-md bg-blue-50 text-blue-700 px-2 py-0.5 text-[11px] font-semibold uppercase font-mono">
          {exchange.method}
        </span>
        <div className="font-mono text-sm text-gray-800 truncate flex-1" title={exchange.url}>
          {exchange.url}
        </div>
        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${statusChipClasses}`}>
          {exchange.responseStatus || "—"}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 hover:border-gray-300 rounded-md px-2 py-1"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" /> Copy as curl
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
        <div className="p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
            Request headers
          </div>
          <HeadersList headers={exchange.requestHeaders} />
          {hasRequestBody && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold pt-2">
                Request body
              </div>
              <pre className="font-mono text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-md p-2 whitespace-pre-wrap break-all max-h-72 overflow-y-auto">
                {exchange.requestBody}
              </pre>
            </>
          )}
        </div>

        <div className="p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
            Response headers
          </div>
          <HeadersList headers={exchange.responseHeaders} />
          <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold pt-2">
            Response body
          </div>
          <BodyPanel body={exchange.responseBodyExcerpt} truncated={exchange.responseBodyTruncated} />
          {!hasResponseBody && (
            <div className="text-[11px] text-gray-400 italic mt-1">
              Body not captured for this exchange.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HttpExchangeCard;
